import { useEffect, useMemo, useState } from "react";
import { Hotel, ArrowRight, Sparkles, Building, Layers, DoorOpen, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * HotelConnectionChart — the "confirmation" screen shown right after a host
 * picks their hotel listing in Step 2 and BEFORE the payment page.
 *
 * Renders a visual 1:1 map:
 *   Property (name · category · unit count)
 *     └── Unit Type A (e.g. "First Floor · Deluxe")
 *           ├── Unit 101  →  Inventory Room 1 of 35   ✓
 *           ├── Unit 102  →  Inventory Room 2 of 35   ✓
 *           └── Unit 103  →  (unmapped — extra unit)
 *     └── Unit Type B ...
 *
 * Variable inventory is fully supported: if OTA has more rooms than PMS
 * units, we simply consume the first N slots; extras stay on OTA. If PMS
 * has more units than OTA rooms, the surplus units render as "unmapped"
 * so the host can spot them at a glance before they pay.
 */
export default function HotelConnectionChart({ property, units, mappings, pool, listingId, onContinue, onBack, busy }) {
  const [unitTypes, setUnitTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/pms/properties/${property.id}/unit-types`);
        setUnitTypes(data?.unit_types || []);
      } catch (_) { setUnitTypes([]); }
      setLoading(false);
    })();
  }, [property.id]);

  const listing = useMemo(
    () => (pool || []).find((p) => p.listing_id === listingId) || {},
    [pool, listingId]
  );
  const inv = Number(listing.total_inventory || 0);
  const pairCount = inv ? Math.min(inv, units.length) : units.length;

  const isReserved = !mappings?.length;
  const orderedUnits = useMemo(
    () => [...units].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [units]
  );

  // Group units by unit type; anything without a type goes into "Unassigned".
  const grouped = useMemo(() => {
    const byUnit = new Map();
    unitTypes.forEach((t) => (t.unit_ids || []).forEach((uid) => byUnit.set(uid, t)));
    const buckets = new Map();
    orderedUnits.forEach((u) => {
      const t = byUnit.get(u.id);
      const key = t?.id || "__unassigned__";
      if (!buckets.has(key)) {
        buckets.set(key, { type: t || { id: "__unassigned__", name: "Unassigned", color: "#94a3b8" }, units: [] });
      }
      buckets.get(key).units.push(u);
    });
    return Array.from(buckets.values());
  }, [orderedUnits, unitTypes]);

  // Slot number is assigned globally in unit-order so the chart is deterministic.
  const slotByUnit = useMemo(() => {
    const map = new Map();
    orderedUnits.forEach((u, i) => map.set(u.id, i < pairCount ? i + 1 : null));
    return map;
  }, [orderedUnits, pairCount]);

  const unmappedUnitCount = orderedUnits.length - pairCount;
  const extraOtaRooms = inv ? Math.max(0, inv - pairCount) : 0;

  return (
    <div data-testid="hotel-connection-chart" className="reveal">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><Layers size={17} /></span>
            Step 2.5 · confirm your connections
          </h2>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Here's how each unit will link to a room from <b>{listing.title || "your hotel listing"}</b>. Review the mapping and continue to billing — the connections lock in the moment your first payment clears.
          </p>
        </div>
        {onBack && (
          <button data-testid="chart-back-btn" onClick={onBack} disabled={busy}
            className="text-xs font-semibold px-4 py-2 rounded-full border border-slate-300 text-slate-500 hover:border-slate-500 hover:text-slate-700 disabled:opacity-50">
            ← Pick a different listing
          </button>
        )}
      </div>

      {/* Top strip — property + listing header cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div className="rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-white p-4">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center"><Building size={17} /></span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase font-semibold tracking-wider text-indigo-500">PMS property</p>
              <p className="font-display font-bold text-slate-900 text-sm truncate">{property.name}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{property.city} · Hotel · <b>{units.length}</b> unit{units.length === 1 ? "" : "s"}</p>
            </div>
          </div>
        </div>
        <div className={`rounded-2xl border-2 ${isReserved ? "border-amber-200 bg-gradient-to-br from-amber-50 via-white to-white" : "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white"} p-4`}>
          <div className="flex items-center gap-3">
            <span className={`h-10 w-10 rounded-xl ${isReserved ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"} flex items-center justify-center`}><Hotel size={17} /></span>
            <div className="min-w-0">
              <p className={`text-[10px] uppercase font-semibold tracking-wider ${isReserved ? "text-amber-600" : "text-emerald-600"}`}>{isReserved ? "reserved OTA listing" : "linked OTA listing"}</p>
              <p className="font-display font-bold text-slate-900 text-sm truncate">{listing.title || "—"}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{listing.city || "—"} · {listing.type || "Hotel"} · <b>{inv || "—"}</b> room{inv === 1 ? "" : "s"} on OTA</p>
            </div>
          </div>
        </div>
      </div>

      {/* Fit summary — reads at a glance */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 mb-5 flex items-center gap-4 flex-wrap text-[12px]">
        <span className="font-semibold text-slate-700 flex items-center gap-1.5">
          <CheckCircle2 size={14} className="text-emerald-500" /> {pairCount} connection{pairCount === 1 ? "" : "s"} ready
        </span>
        {unmappedUnitCount > 0 && (
          <span data-testid="chart-warning-extra-units" className="font-semibold text-amber-700 flex items-center gap-1.5">
            <AlertTriangle size={14} /> {unmappedUnitCount} unit{unmappedUnitCount === 1 ? "" : "s"} without an OTA room · won't receive OTA bookings
          </span>
        )}
        {extraOtaRooms > 0 && (
          <span data-testid="chart-info-extra-inventory" className="font-semibold text-slate-500 flex items-center gap-1.5">
            <Sparkles size={13} /> {extraOtaRooms} spare OTA room{extraOtaRooms === 1 ? "" : "s"} · kept in reserve on Atiti OTA
          </span>
        )}
      </div>

      {/* The chart itself — grouped by unit type */}
      {loading ? (
        <div className="text-center py-8 text-slate-400"><Loader2 className="animate-spin inline mr-2" size={14} /> loading unit types…</div>
      ) : (
        <div className="space-y-4">
          {grouped.map(({ type, units: gu }) => (
            <div key={type.id} data-testid={`chart-type-${type.id}`}
              className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                <span className="h-5 w-5 rounded" style={{ background: type.color || "#94a3b8" }} />
                <p className="font-display font-semibold text-slate-800 text-sm">{type.name}</p>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 ml-auto">
                  {gu.length} unit{gu.length === 1 ? "" : "s"}
                </span>
              </div>
              <ul className="divide-y divide-slate-100">
                {gu.map((u) => {
                  const slot = slotByUnit.get(u.id);
                  const linked = !!slot;
                  return (
                    <li key={u.id} data-testid={`chart-row-${u.id}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
                      <span className="h-7 w-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                        <DoorOpen size={13} />
                      </span>
                      <span className="font-semibold text-slate-900 min-w-[8ch]">{u.name}</span>
                      <ArrowRight size={13} className="text-slate-300" />
                      {linked ? (
                        <span className="text-slate-600">
                          Room <b className="text-slate-900">{slot}</b>{inv ? <> of <b className="text-slate-900">{inv}</b></> : null} · <span className="text-slate-500">{listing.title}</span>
                        </span>
                      ) : (
                        <span className="text-amber-700 font-semibold flex items-center gap-1">
                          <AlertTriangle size={12} /> no OTA room · direct bookings only
                        </span>
                      )}
                      {linked && <CheckCircle2 size={13} className="text-emerald-500 ml-auto shrink-0" />}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 flex items-center justify-end gap-2 flex-wrap">
        <button data-testid="chart-continue-btn" onClick={onContinue} disabled={busy}
          className="btn-brand rounded-full px-6 py-3 text-sm font-semibold flex items-center gap-2">
          {busy ? <Loader2 className="animate-spin" size={15} /> : null}
          Looks good · continue to billing <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}
